import { IPartFinderFamilyAttribute } from 'companySettings/types';

// A group needs at least two attributes: one left alone is a plain attribute again.
export const MIN_GROUP_SIZE = 2;

// What the attribute list shows, in order: a plain attribute, or a group with its attributes. Each
// carries the attributes' indexes in the family's order.
export type IAttributeUnit =
  | { type: 'attribute'; index: number }
  | { type: 'group'; name: string; indexes: number[] };

export const getGroupKey = (name: string) => name.trim().toLowerCase();

// The attributes of a group sit next to each other, so a run of the same group name is one group.
export const getAttributeUnits = (attributes: IPartFinderFamilyAttribute[]): IAttributeUnit[] =>
  attributes.reduce<IAttributeUnit[]>((units, attribute, index) => {
    const previous = units[units.length - 1];

    if (
      attribute.group &&
      'group' === previous?.type &&
      getGroupKey(previous.name) === getGroupKey(attribute.group)
    ) {
      previous.indexes.push(index);

      return units;
    }

    return [
      ...units,
      attribute.group
        ? { type: 'group', name: attribute.group, indexes: [index] }
        : { type: 'attribute', index },
    ];
  }, []);

export const getUnitIndexes = (unit: IAttributeUnit) =>
  'group' === unit.type ? unit.indexes : [unit.index];

// Lets a group with a single attribute go, leaving every attribute where it is.
export const dropSingleGroups = (attributes: IPartFinderFamilyAttribute[]) => {
  const sizes = new Map<string, number>();

  attributes.forEach(({ group }) => {
    if (group) {
      sizes.set(getGroupKey(group), (sizes.get(getGroupKey(group)) ?? 0) + 1);
    }
  });

  return attributes.map((attribute) =>
    attribute.group && (sizes.get(getGroupKey(attribute.group)) ?? 0) < MIN_GROUP_SIZE
      ? { ...attribute, group: null }
      : attribute
  );
};

// Brings each group's attributes together where the group first appears, keeping their order,
// and lets a group with a single attribute go.
export const normalizeGroups = (attributes: IPartFinderFamilyAttribute[]) => {
  const kept = dropSingleGroups(attributes);
  const placed = new Set<string>();

  return kept.flatMap((attribute) => {
    if (!attribute.group) {
      return [attribute];
    }

    const key = getGroupKey(attribute.group);

    if (placed.has(key)) {
      return [];
    }

    placed.add(key);

    return kept.filter(({ group }) => group && getGroupKey(group) === key);
  });
};

// The selected attributes become the group, where the first of them is.
export const groupAttributes = (
  attributes: IPartFinderFamilyAttribute[],
  indexes: number[],
  name: string
) =>
  normalizeGroups(
    attributes.map((attribute, index) =>
      indexes.includes(index) ? { ...attribute, group: name.trim() } : attribute
    )
  );

export const renameGroup = (attributes: IPartFinderFamilyAttribute[], from: string, to: string) =>
  attributes.map((attribute) =>
    attribute.group && getGroupKey(attribute.group) === getGroupKey(from)
      ? { ...attribute, group: to.trim() }
      : attribute
  );

// Ungroup: the group's attributes stay where they are, as plain attributes.
export const ungroup = (attributes: IPartFinderFamilyAttribute[], name: string) =>
  attributes.map((attribute) =>
    attribute.group && getGroupKey(attribute.group) === getGroupKey(name)
      ? { ...attribute, group: null }
      : attribute
  );

// Detach from group: the attribute goes right after the group, on its own.
export const detachFromGroup = (attributes: IPartFinderFamilyAttribute[], index: number) => {
  const detached = { ...attributes[index], group: null };
  const rest = attributes.filter((_, current) => current !== index);
  const key = getGroupKey(attributes[index].group ?? '');
  const lastIndex = rest.reduce(
    (last, { group }, current) => (group && getGroupKey(group) === key ? current : last),
    index - 1
  );

  return normalizeGroups([...rest.slice(0, lastIndex + 1), detached, ...rest.slice(lastIndex + 1)]);
};

// Move to group: the attribute joins the group as its last attribute.
export const moveToGroup = (
  attributes: IPartFinderFamilyAttribute[],
  index: number,
  name: string
) => {
  const group = attributes.find(
    (attribute) => attribute.group && getGroupKey(attribute.group) === getGroupKey(name)
  )?.group;
  const moved = { ...attributes[index], group };
  const rest = attributes.filter((_, current) => current !== index);
  const lastIndex = rest.reduce(
    (last, attribute, current) =>
      attribute.group && getGroupKey(attribute.group) === getGroupKey(name) ? current : last,
    rest.length - 1
  );

  return normalizeGroups([...rest.slice(0, lastIndex + 1), moved, ...rest.slice(lastIndex + 1)]);
};

// Moves a whole attribute or group to another one's place in the list.
export const moveUnit = (attributes: IPartFinderFamilyAttribute[], from: number, to: number) => {
  const units = getAttributeUnits(attributes);
  const moved = units.splice(from, 1);

  units.splice(to, 0, ...moved);

  return units.flatMap((unit) => getUnitIndexes(unit).map((index) => attributes[index]));
};

// Moves an attribute within its group.
export const moveWithinGroup = (
  attributes: IPartFinderFamilyAttribute[],
  indexes: number[],
  from: number,
  to: number
) => {
  const members = indexes.map((index) => attributes[index]);
  const [moved] = members.splice(from, 1);

  members.splice(to, 0, moved);

  return attributes.map((attribute, index) =>
    indexes.includes(index) ? members[indexes.indexOf(index)] : attribute
  );
};
